import { useState, useEffect, useRef } from 'react';
import { 
  Heart, MessageCircle, UserPlus, UserCheck, AtSign, Tag, Hash, Calendar, 
  CalendarPlus, Bell, Check, CheckCheck, Trash2, Filter, RefreshCw, Loader2,
  ChevronRight, X, Plus, Search, Ticket, Reply
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { useCurrentUser } from '../../../hooks/useCurrentUser';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Mapping des icônes
const ICON_MAP = {
  'heart': Heart,
  'message-circle': MessageCircle,
  'user-plus': UserPlus,
  'user-check': UserCheck,
  'at-sign': AtSign,
  'tag': Tag,
  'hash': Hash,
  'calendar': Calendar,
  'calendar-plus': CalendarPlus,
  'ticket': Ticket,
  'reply': Reply,
};

// Couleurs par type - Charte Abracadabâti (bleu marine #1E3A5F, orange #F97316)
const COLOR_MAP = {
  'red': 'bg-red-50 text-red-600',
  'blue': 'bg-[#F0F4F8] text-[#1E3A5F]',
  'purple': 'bg-[#F0F4F8] text-[#1E3A5F]',  // Remplacé par bleu marine
  'orange': 'bg-[#FFF8F4] text-[#F97316]',
  'green': 'bg-[#1E3A5F]/10 text-[#1E3A5F]',
  'cyan': 'bg-[#F0F4F8] text-[#1E3A5F]',  // Remplacé par bleu marine
  'indigo': 'bg-[#F0F4F8] text-[#1E3A5F]', // Remplacé par bleu marine
  'pink': 'bg-[#FFF8F4] text-[#F97316]',   // Remplacé par orange
  'yellow': 'bg-amber-50 text-amber-600',
  'emerald': 'bg-slate-100 text-slate-600',
};

const FILTERS = [
  { id: 'all', label: 'Toutes', icon: Bell },
  { id: 'like', label: 'J\'aime', icon: Heart },
  { id: 'comment', label: 'Commentaires', icon: MessageCircle },
  { id: 'follow', label: 'Abonnés', icon: UserPlus },
  { id: 'new_ticket', label: 'Demandes', icon: Ticket },
  { id: 'mention', label: 'Mentions', icon: AtSign },
  { id: 'salon_message', label: 'Salons', icon: Hash },
];

function NotificationItem({ notification, onMarkRead, onAcceptFollow, onDeclineFollow, onDelete }) {
  const Icon = ICON_MAP[notification.type_info?.icon] || Bell;
  const colorClass = COLOR_MAP[notification.type_info?.color] || 'bg-gray-100 text-gray-600';
  
  const isFollowRequest = notification.type === 'follow_request';

  return (
    <div className={`flex items-start gap-4 p-4 rounded-xl transition-all ${
      notification.read ? 'bg-white' : 'bg-[#FFF8F4] border border-[#F97316]/20'
    } hover:shadow-md group`}>
      {/* Avatar avec indicateur de type */}
      <div className="relative">
        <div className="w-12 h-12 bg-gradient-to-br from-[#1E3A5F] to-[#2D4A6F] rounded-full flex items-center justify-center text-white font-semibold">
          {notification.actor.avatar}
        </div>
        <div className={`absolute -bottom-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center ${colorClass}`}>
          <Icon size={12} />
        </div>
      </div>

      {/* Contenu */}
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-900">
          <span className="font-semibold">{notification.actor.name}</span>
          {' '}
          <span className="text-gray-600">{notification.type_info?.label}</span>
          {notification.content && (
            <>
              {' '}
              <span className="text-gray-900">{notification.content}</span>
            </>
          )}
        </p>
        <p className="text-xs text-gray-500 mt-1">{notification.time}</p>

        {/* Actions pour les demandes d'abonnement */}
        {isFollowRequest && !notification.accepted && !notification.declined && (
          <div className="flex gap-2 mt-3">
            <button
              onClick={() => onAcceptFollow(notification.id)}
              className="px-4 py-1.5 bg-[#F97316] hover:bg-[#EA580C] text-white text-sm font-medium rounded-lg transition-colors"
            >
              Accepter
            </button>
            <button
              onClick={() => onDeclineFollow(notification.id)}
              className="px-4 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-700 text-sm font-medium rounded-lg transition-colors"
            >
              Refuser
            </button>
          </div>
        )}
        {isFollowRequest && notification.accepted && (
          <p className="text-xs text-[#1E3A5F] mt-2 flex items-center gap-1">
            <Check size={12} /> Accepté
          </p>
        )}
      </div>

      {/* Actions hover */}
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        {!notification.read && (
          <button
            onClick={() => onMarkRead(notification.id)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Marquer comme lu"
          >
            <Check size={16} className="text-gray-400" />
          </button>
        )}
        <button
          onClick={() => onDelete(notification.id)}
          className="p-2 hover:bg-red-50 rounded-lg transition-colors"
          title="Supprimer"
        >
          <Trash2 size={16} className="text-gray-400 hover:text-red-500" />
        </button>
        <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
          <ChevronRight size={16} className="text-gray-400" />
        </button>
      </div>

      {/* Indicateur non lu */}
      {!notification.read && (
        <div className="w-2 h-2 bg-[#F97316] rounded-full flex-shrink-0 mt-2" />
      )}
    </div>
  );
}

function NotificationGroup({ title, notifications, ...props }) {
  if (!notifications || notifications.length === 0) return null;

  return (
    <div className="mb-6">
      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 px-1">
        {title}
      </h3>
      <div className="space-y-2">
        {notifications.map(notif => (
          <NotificationItem key={notif.id} notification={notif} {...props} />
        ))}
      </div>
    </div>
  );
}

export default function NotificationsPage() {
  const { user } = useCurrentUser();
  const userId = user?.id || 'demo_user';
  
  const [notifications, setNotifications] = useState({ today: [], yesterday: [], this_week: [], earlier: [] });
  const [stats, setStats] = useState({ unread: 0, by_type: {} });
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [showScrollHint, setShowScrollHint] = useState(true);
  const scrollContainerRef = useRef(null);

  // Masquer l'indicateur de scroll après le premier scroll
  const handleScroll = () => {
    if (showScrollHint) setShowScrollHint(false);
  };

  const fetchNotifications = async () => {
    try {
      const url = activeFilter === 'all' 
        ? `${API_URL}/api/cockpit/notifications?user_id=${userId}`
        : `${API_URL}/api/cockpit/notifications?user_id=${userId}&type_filter=${activeFilter}`;
      
      const response = await fetch(url);
      const data = await response.json();
      setNotifications(data.groups || {});
      setStats(data.stats || { unread: 0, by_type: {} });
    } catch (error) {
      console.error('Erreur chargement notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [activeFilter, userId]);

  const markAsRead = async (id) => {
    try {
      await fetch(`${API_URL}/api/cockpit/notifications/${id}/read?user_id=${userId}`, { method: 'PATCH' });
      fetchNotifications();
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await fetch(`${API_URL}/api/cockpit/notifications/read-all?user_id=${userId}`, { method: 'PATCH' });
      fetchNotifications();
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const acceptFollow = async (id) => {
    try {
      await fetch(`${API_URL}/api/cockpit/notifications/accept-follow/${id}?user_id=${userId}`, { method: 'POST' });
      fetchNotifications();
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const declineFollow = async (id) => {
    try {
      await fetch(`${API_URL}/api/cockpit/notifications/decline-follow/${id}?user_id=${userId}`, { method: 'POST' });
      fetchNotifications();
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const deleteNotification = async (id) => {
    try {
      await fetch(`${API_URL}/api/cockpit/notifications/${id}?user_id=${userId}`, { method: 'DELETE' });
      fetchNotifications();
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const totalNotifications = Object.values(notifications).flat().length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#1E3A5F]" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header - Style harmonisé comme Mes Messages */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-1">
          <div>
            <h1 className="text-[16px] sm:text-xl font-bold text-[#1e3a5f]">
              Notifications
            </h1>
            <p className="text-sm text-gray-500">
              {stats.unread > 0 ? `${stats.unread} non lues` : 'Toutes les notifications sont lues'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button 
              onClick={fetchNotifications}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <RefreshCw size={18} className="text-gray-500" />
            </button>
            {stats.unread > 0 && (
              <Button
                onClick={markAllAsRead}
                className="bg-orange-500 hover:bg-orange-600 text-white rounded-xl gap-2 h-9 px-4 text-sm font-semibold shadow-sm"
              >
                <CheckCheck size={16} />
                <span className="hidden sm:inline">Tout lu</span>
              </Button>
            )}
          </div>
        </div>

        {/* Stats rapides */}
        <div className="flex items-center gap-4 mt-3">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white border border-gray-100">
            <Bell size={14} className="text-orange-500" />
            <span className="text-xs font-medium text-gray-600">
              {totalNotifications} notification{totalNotifications > 1 ? 's' : ''}
            </span>
          </div>
          {stats.unread > 0 && (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-orange-50 border border-orange-100">
              <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse" />
              <span className="text-xs font-medium text-orange-700">
                {stats.unread} non lue{stats.unread > 1 ? 's' : ''}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Filtres - Style harmonisé comme Mes Messages (une seule ligne scrollable) */}
      <div className="flex-shrink-0 mb-4 relative">
        <div 
          ref={scrollContainerRef}
          onScroll={handleScroll}
          className="flex gap-1.5 overflow-x-auto scrollbar-hide"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {FILTERS.map(filter => {
            const FilterIcon = filter.icon;
            const count = filter.id === 'all' ? stats.unread : (stats.by_type[filter.id] || 0);
            
            return (
              <button
                key={filter.id}
                onClick={() => setActiveFilter(filter.id)}
                className={`flex-shrink-0 px-3 py-2 rounded-xl text-xs font-semibold transition-all whitespace-nowrap ${
                  activeFilter === filter.id
                    ? 'bg-orange-500 text-white shadow-sm'
                    : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                }`}
              >
                <span className="flex items-center gap-1">
                  {filter.label}
                  {count > 0 && (
                    <span className={`min-w-[18px] h-[18px] flex items-center justify-center text-[10px] rounded-full ${
                      activeFilter === filter.id 
                        ? 'bg-white/25 text-white' 
                        : 'bg-orange-500 text-white'
                    }`}>
                      {count}
                    </span>
                  )}
                </span>
              </button>
            );
          })}
          
          {/* Espace pour la flèche */}
          <div className="flex-shrink-0 w-6" />
        </div>
        
        {/* Flèche orange discrète pour indiquer le scroll */}
        {showScrollHint && (
          <div className="absolute right-0 top-1/2 -translate-y-1/2 pointer-events-none flex items-center">
            <ChevronRight size={18} className="text-orange-500" strokeWidth={2.5} />
          </div>
        )}
      </div>

      {/* Contenu principal */}
      <div className="flex-1 rounded-2xl overflow-hidden bg-white border border-gray-100">
        {totalNotifications === 0 ? (
          /* État vide - Style harmonisé */
          <div className="h-full flex items-center justify-center">
            <div className="text-center p-8 max-w-sm">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 bg-gray-50">
                <Bell size={28} className="text-gray-300" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Aucune notification
              </h3>
              <p className="text-sm text-gray-500">
                Tu es à jour ! Les nouvelles notifications apparaîtront ici.
              </p>
            </div>
          </div>
        ) : (
          <div className="p-4 overflow-y-auto h-full">
            <NotificationGroup 
              title="Aujourd'hui" 
              notifications={notifications.today}
              onMarkRead={markAsRead}
              onAcceptFollow={acceptFollow}
              onDeclineFollow={declineFollow}
              onDelete={deleteNotification}
            />
            <NotificationGroup 
              title="Hier" 
              notifications={notifications.yesterday}
              onMarkRead={markAsRead}
              onAcceptFollow={acceptFollow}
              onDeclineFollow={declineFollow}
              onDelete={deleteNotification}
            />
            <NotificationGroup 
              title="Cette semaine" 
              notifications={notifications.this_week}
              onMarkRead={markAsRead}
              onAcceptFollow={acceptFollow}
              onDeclineFollow={declineFollow}
              onDelete={deleteNotification}
            />
            <NotificationGroup 
              title="Plus ancien" 
              notifications={notifications.earlier}
              onMarkRead={markAsRead}
              onAcceptFollow={acceptFollow}
              onDeclineFollow={declineFollow}
              onDelete={deleteNotification}
            />
          </div>
        )}
      </div>
    </div>
  );
}
